import React from 'react';
import { Link } from 'react-router-dom';
import { Award, ShieldCheck, Heart, Brain, Users, CheckCircle2, ArrowRight, Sparkles, Globe } from 'lucide-react';
export function AboutPage() {
  return <>
      {/* Hero — Green Goddess style: large image with overlay text */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-end overflow-hidden" data-id="element-540">
        <div className="absolute inset-0" data-id="element-541">
          <img src="https://cdn.magicpatterns.com/uploads/6rUsfeZ8rFCHJMfqct3HKx/em.About_.1.webp" alt="Esther Mensah" className="w-full h-full object-cover object-top" data-id="element-542" />
          <div className="absolute inset-0 bg-gradient-to-t from-olive/90 via-olive/40 to-transparent" data-id="element-543" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20 w-full" data-id="element-544">
          <div data-id="element-545">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05]" data-id="element-546">
              heal well. <span className="italic text-cream" data-id="element-547">feel alive.</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl mt-4 max-w-lg font-medium" data-id="element-548">
              Compassionate therapy rooted in understanding, culture, and
              evidence.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section — cream background */}
      <section className="py-16 lg:py-20 bg-cream" data-id="element-549">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-550">
          <div data-id="element-551">
            <span className="text-olive font-bold uppercase tracking-widest text-xs mb-4 block" data-id="element-552">
              ABOUT ESTHER MENSAH
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-charcoal mb-6 leading-tight" data-id="element-553">
              Compassionate Therapy for Those Who Want to{' '}
              <span className="italic text-gold" data-id="element-554">Feel Whole Again</span>
            </h2>
          </div>
          <div data-id="element-555">
            <p className="text-charcoal/80 text-base md:text-lg leading-relaxed mb-4 font-medium max-w-3xl mx-auto" data-id="element-556">
              You don't need another surface-level fix or a list of "just think
              positive" tips. You need a safe, culturally sensitive space where
              you can show up as you are — and work through what's been weighing
              you down. I help individuals, couples, and families navigate PTSD,
              trauma, grief, anxiety, depression, and relationship challenges
              with care, understanding, and evidence-based guidance.
            </p>
            <p className="text-charcoal/80 text-base md:text-lg leading-relaxed mb-8 font-medium max-w-3xl mx-auto" data-id="element-557">
              My name is Esther Mensah. I'm a Registered Social Worker and
              Psychotherapist based in London, Ontario, and I also offer virtual
              therapy across the province. My practice is built on the belief
              that healing is possible for everyone — and that it starts with
              being truly seen and heard.
            </p>
          </div>
          <div data-id="element-558">
            <Link to="/booking" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-8 py-3.5 rounded-full font-bold text-base transition-all shadow-soft hover:shadow-soft-lg min-h-[48px] tracking-wide" data-id="element-559">
              Book a Free Consultation
              <ArrowRight size={16} className="ml-2" data-id="element-560" />
            </Link>
          </div>
        </div>
      </section>

      {/* Approach Section — styled like Green Goddess template */}
      <section className="py-16 lg:py-20 bg-olive/10" data-id="element-561">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-562">
          <div className="text-center mb-12" data-id="element-563">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal" data-id="element-564">
              the <span className="italic text-olive" data-id="element-565">Esther Mensah</span>{' '}
              approach
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-566">
            <div data-id="element-567">
              <div className="bg-white rounded-2xl p-8 text-center h-full border border-olive/10" data-id="element-568">
                <div className="w-16 h-16 rounded-2xl bg-olive/10 flex items-center justify-center mx-auto mb-5" data-id="element-569">
                  <Heart size={28} className="text-olive" strokeWidth={1.5} data-id="element-570" />
                </div>
                <h3 className="font-heading text-lg font-bold text-charcoal mb-3" data-id="element-571">
                  Culturally Sensitive
                </h3>
                <p className="text-charcoal/70 text-base font-medium leading-relaxed" data-id="element-572">
                  I honour your cultural identity and lived experiences. Therapy
                  is tailored to reflect who you are — your background, values,
                  and worldview — so you feel truly understood.
                </p>
              </div>
            </div>

            <div data-id="element-573">
              <div className="bg-white rounded-2xl p-8 text-center h-full border border-olive/10" data-id="element-574">
                <div className="w-16 h-16 rounded-2xl bg-olive/10 flex items-center justify-center mx-auto mb-5" data-id="element-575">
                  <Brain size={28} className="text-olive" strokeWidth={1.5} data-id="element-576" />
                </div>
                <h3 className="font-heading text-lg font-bold text-charcoal mb-3" data-id="element-577">
                  Evidence-Based
                </h3>
                <p className="text-charcoal/70 text-base font-medium leading-relaxed" data-id="element-578">
                  I use proven therapeutic methods including EMDR, CBT, and
                  trauma-informed care. Every approach is grounded in research
                  and adapted to your unique needs and goals.
                </p>
              </div>
            </div>

            <div data-id="element-579">
              <div className="bg-white rounded-2xl p-8 text-center h-full border border-olive/10" data-id="element-580">
                <div className="w-16 h-16 rounded-2xl bg-olive/10 flex items-center justify-center mx-auto mb-5" data-id="element-581">
                  <Users size={28} className="text-olive" strokeWidth={1.5} data-id="element-582" />
                </div>
                <h3 className="font-heading text-lg font-bold text-charcoal mb-3" data-id="element-583">
                  Compassionate Care
                </h3>
                <p className="text-charcoal/70 text-base font-medium leading-relaxed" data-id="element-584">
                  My practice is a safe, non-judgmental space where you can be
                  vulnerable. I walk alongside you with empathy, patience, and
                  genuine care for your wellbeing.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12" data-id="element-585">
            <div data-id="element-586">
              <div className="bg-white rounded-2xl p-8 text-center h-full border border-olive/10" data-id="element-587">
                <div className="w-16 h-16 rounded-2xl bg-olive/10 flex items-center justify-center mx-auto mb-5" data-id="element-588">
                  <Sparkles size={28} className="text-olive" strokeWidth={1.5} data-id="element-589" />
                </div>
                <h3 className="font-heading text-lg font-bold text-charcoal mb-3" data-id="element-590">
                  Empowering
                </h3>
                <p className="text-charcoal/70 text-base font-medium leading-relaxed" data-id="element-591">
                  I support clients to build their own strength, resilience, and
                  capacity for healing. You are the expert of your own life —
                  I'm here to help you rediscover that.
                </p>
              </div>
            </div>

            <div data-id="element-592">
              <div className="bg-white rounded-2xl p-8 text-center h-full border border-olive/10" data-id="element-593">
                <div className="w-16 h-16 rounded-2xl bg-olive/10 flex items-center justify-center mx-auto mb-5" data-id="element-594">
                  <Globe size={28} className="text-olive" strokeWidth={1.5} data-id="element-595" />
                </div>
                <h3 className="font-heading text-lg font-bold text-charcoal mb-3" data-id="element-596">
                  Inclusive
                </h3>
                <p className="text-charcoal/70 text-base font-medium leading-relaxed" data-id="element-597">
                  I create a safe, non-judgmental space for all, regardless of
                  background, identity, or life circumstance. Everyone deserves
                  access to quality mental health care.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center" data-id="element-598">
            <Link to="/services" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-8 py-3.5 rounded-full font-bold text-base transition-all shadow-soft min-h-[48px] tracking-wide" data-id="element-599">
              Explore Our Services
              <ArrowRight size={16} className="ml-2" data-id="element-600" />
            </Link>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-601">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-602">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-603">
            <span className="text-olive font-bold uppercase tracking-widest text-xs mb-4 block" data-id="element-604">
              CREDENTIALS & TRAINING
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mb-6 leading-tight" data-id="element-605">
              Qualified, Registered &{' '}
              <span className="italic text-gold" data-id="element-606">Committed to Excellence</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-id="element-607">
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-608">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-609">
                <Award size={20} data-id="element-610" />
              </div>
              <div data-id="element-611">
                <h3 className="font-bold text-charcoal text-base" data-id="element-612">
                  MSc Clinical Psychology
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-613">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-614">
                <ShieldCheck size={20} data-id="element-615" />
              </div>
              <div data-id="element-616">
                <h3 className="font-bold text-charcoal text-base" data-id="element-617">
                  Registered Social Worker (RSW)
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-618">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-619">
                <ShieldCheck size={20} data-id="element-620" />
              </div>
              <div data-id="element-621">
                <h3 className="font-bold text-charcoal text-base" data-id="element-622">
                  Registered Psychotherapist (RP)
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-623">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-624">
                <CheckCircle2 size={20} data-id="element-625" />
              </div>
              <div data-id="element-626">
                <h3 className="font-bold text-charcoal text-base" data-id="element-627">
                  CRPO Registered
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-628">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-629">
                <Brain size={20} data-id="element-630" />
              </div>
              <div data-id="element-631">
                <h3 className="font-bold text-charcoal text-base" data-id="element-632">
                  EMDR Trained Therapist
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-633">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-634">
                <Heart size={20} data-id="element-635" />
              </div>
              <div data-id="element-636">
                <h3 className="font-bold text-charcoal text-base" data-id="element-637">
                  Trauma-Informed Care Certified
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-cream rounded-xl p-4" data-id="element-638">
              <div className="w-10 h-10 rounded-lg bg-olive/10 flex items-center justify-center text-olive flex-shrink-0" data-id="element-639">
                <Brain size={20} data-id="element-640" />
              </div>
              <div data-id="element-641">
                <h3 className="font-bold text-charcoal text-base" data-id="element-642">
                  CBT Trained
                </h3>
              </div>
            </div>
          </div>

          <div className="text-center" data-id="element-643">
            <Link to="/booking" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-8 py-3.5 rounded-full font-bold text-base transition-all shadow-soft min-h-[48px] tracking-wide" data-id="element-644">
              Book a Free Consultation
              <ArrowRight size={16} className="ml-2" data-id="element-645" />
            </Link>
          </div>
        </div>
      </section>

      {/* Outside of Work Section */}
      <section className="py-16 lg:py-24 bg-white border-t border-charcoal/5" data-id="element-646">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-647">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-12" data-id="element-648">
            <div className="order-2 lg:order-1" data-id="element-649">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-6" data-id="element-650">
                Beyond the Practice
              </h2>
              <p className="text-charcoal/80 text-lg leading-relaxed mb-6 font-medium" data-id="element-651">
                Outside of the therapy room, Esther is passionate about
                community wellness, cultural advocacy, and spending quality time
                with family. She enjoys reading, exploring nature, volunteering
                with local mental health initiatives, and connecting with her
                cultural roots.
              </p>
              <p className="text-charcoal/80 text-lg leading-relaxed font-medium" data-id="element-652">
                Esther believes that personal wellness and self-care are
                essential to being an effective therapist — and she practices
                what she preaches.
              </p>
            </div>
            <div className="order-1 lg:order-2 aspect-[4/3] rounded-2xl overflow-hidden shadow-soft-lg" data-id="element-653">
              <img src="https://cdn.magicpatterns.com/uploads/qiTTbGAsY9FdrtWcC9QuVT/ChatGPT_Image_Mar_13,_2026,_04_00_19_PM.png" alt="Esther Mensah outside of work" className="w-full h-full object-cover object-top" data-id="element-654" />
            </div>
          </div>

          <div className="text-center" data-id="element-655">
            <Link to="/contact" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-8 py-3.5 rounded-full font-bold text-base transition-all shadow-soft min-h-[48px] tracking-wide" data-id="element-656">
              Get in Touch
              <ArrowRight size={16} className="ml-2" data-id="element-657" />
            </Link>
          </div>
        </div>
      </section>

      {/* Together We Make a Difference Section - Ribbon/Trust Badge Style */}
      <section className="py-20 lg:py-28 bg-olive relative overflow-hidden" data-id="element-658">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-659">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full border-[40px] border-white" data-id="element-660"></div>
          <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full border-[40px] border-white" data-id="element-661"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-662">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-663">
            <div className="order-2 lg:order-1 relative" data-id="element-664">
              <div className="aspect-[4/5] max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20" data-id="element-665">
                <img src="https://cdn.magicpatterns.com/uploads/3Xwrbr68chFxqgiLU4krLR/Together_Esther.png" alt="Esther Mensah" className="w-full h-full object-cover object-top" data-id="element-666" />
              </div>

              {/* Floating animated badge */}
              <div className="absolute -bottom-6 -right-6 lg:-right-12 bg-white rounded-2xl p-6 shadow-xl border border-charcoal/5 max-w-[240px] animate-[bounce_4s_ease-in-out_infinite]" data-id="element-667">
                <div className="flex items-center gap-4 mb-2" data-id="element-668">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold" data-id="element-669">
                    <Heart size={24} fill="currentColor" data-id="element-670" />
                  </div>
                  <div data-id="element-671">
                    <div className="font-heading font-bold text-2xl text-charcoal leading-none" data-id="element-672">
                      500+
                    </div>
                    <div className="text-xs font-bold text-charcoal/60 uppercase tracking-wide mt-1" data-id="element-673">
                      Clients Helped
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 text-white" data-id="element-674">
              <span className="inline-block bg-white/20 backdrop-blur-sm text-white font-bold uppercase tracking-widest text-xs px-4 py-1.5 rounded-full mb-6 border border-white/30" data-id="element-675">
                OUR IMPACT
              </span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white" data-id="element-676">
                Together, we make a{' '}
                <span className="text-gold italic" data-id="element-677">difference</span>
              </h2>
              <p className="text-white/80 text-lg md:text-xl font-medium leading-relaxed mb-10" data-id="element-678">
                Supporting the mental health and well-being of individuals and
                families in London, Ontario and across the province.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-10" data-id="element-679">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-680">
                  <div className="text-4xl font-heading font-bold text-gold mb-2" data-id="element-681">
                    10+
                  </div>
                  <p className="text-white/80 text-sm font-medium" data-id="element-682">
                    Years of clinical experience
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-683">
                  <div className="text-4xl font-heading font-bold text-gold mb-2" data-id="element-684">
                    5
                  </div>
                  <p className="text-white/80 text-sm font-medium" data-id="element-685">
                    Specialized approaches
                  </p>
                </div>
              </div>

              <Link to="/booking" className="inline-flex items-center justify-center bg-white hover:bg-cream text-olive px-8 py-4 rounded-full font-bold text-base transition-all shadow-lg min-h-[52px] tracking-wide" data-id="element-686">
                Start Your Journey Today
                <ArrowRight size={16} className="ml-2" data-id="element-687" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 lg:py-20 bg-cream" data-id="element-688">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-689">
          <div data-id="element-690">
            <div className="w-16 h-16 rounded-full bg-olive/10 flex items-center justify-center mx-auto mb-6" data-id="element-691">
              <Heart size={28} className="text-olive" data-id="element-692" />
            </div>
            <blockquote className="font-heading text-2xl md:text-3xl font-bold text-charcoal leading-snug mb-6 italic" data-id="element-693">
              "Healing doesn't mean the damage never existed. It means the
              damage no longer controls your life."
            </blockquote>
            <p className="text-charcoal/70 text-base font-medium mb-8" data-id="element-694">
              I believe that with the right support, every person has the
              capacity to heal, grow, and thrive. Your story matters, and I'm
              honoured to be part of your journey.
            </p>
            <Link to="/booking" className="inline-flex items-center justify-center bg-olive text-white px-8 py-3.5 rounded-full font-bold text-base transition-all hover:bg-olive/90 min-h-[48px] tracking-wide shadow-md" data-id="element-695">
              Start Your Healing Journey
              <ArrowRight size={16} className="ml-2" data-id="element-696" />
            </Link>
          </div>
        </div>
      </section>
    </>;
}